<template>
  <div :class="classes()">
    <Label v-if="testing && task.taskId" :class="`${el}__debug`" :label="task.taskId" />
    <template v-if="hasSubTasks">
      <div :class="`${el}__container`" @click="handleClick">
        <div :class="`${el}__indicator`">
          <BaseRadio v-if="!isCollapsible" />
          <Doughnut
            v-else
            :class="`${el}__doughnut`"
            :width="32"
            :height="32"
            :chartData="{
              datasets: [
                {
                  data: [chckedSubtasks, countSubtasks - chckedSubtasks],
                  backgroundColor: [colorSupportSuccess, 'rgba(179, 206, 218, 0.6)'],
                },
              ],
            }"
            :options="{
              cutoutPercentage: 60,
              responsive: true,
              elements: {
                arc: {
                  borderWidth: 0,
                },
              },
              title: {
                display: false,
              },
              legend: {
                display: false,
              },
              tooltips: {
                enabled: false,
              },
              hover: { mode: null },
            }"
          />
        </div>
        <div :class="`${el}__inner`">
          <div :class="`${el}__content`">
            <div :class="`${el}__headline`">
              <Headline v-if="current && $matchMedia.l" size="xs" noMargin>
                {{ task.headline }}
              </Headline>
              <Copy v-else size="l" element="div" color="primaryBlue" bold>
                {{ task.headline }}
              </Copy>
              <Label
                v-if="task.optional"
                :label="$t('taskScheduler.label.optional')"
                color="secondaryYellow"
              />
            </div>

            <template v-if="current && visibleSubtask">
              <Copy
                v-if="!isCollapsible"
                :staticClass="`${el}__subline`"
                element="div"
                color="primaryBlue"
                bold
              >
                {{ visibleSubtask.headline }}
              </Copy>
              <Copy :element="isCollapsible ? 'p' : 'div'" color="tertiaryLight">
                {{ visibleSubtask.copy }}
              </Copy>
            </template>
          </div>
          <div :class="`${el}__edit`">
            <div v-if="isCollapsible" :class="`${el}__collapsible`">
              <Copy element="div" color="primaryBlue" bold>
                {{ $t('taskScheduler.collapsibleTasks', { chckedSubtasks, countSubtasks }) }}
              </Copy>
              <Icon
                :class="`${el}__collapsibleIcon`"
                name="arrow-down-s-line"
                color="primaryBlueLight"
              />
            </div>
            <Link v-else icon="arrow-right-line" iconPos="right">
              {{ $t('taskScheduler.edit') }}
            </Link>
          </div>
        </div>
      </div>
      <div
        v-if="isCollapsible"
        :class="`${el}__subTasks`"
        ref="subTasks"
        :style="{ maxHeight: `${collapsibleHeight * 1.2}px` }"
      >
        <div :class="`${el}__subTaskWrapper`">
          <div
            v-for="(subTask, idx) in task.subTasks"
            :key="`subtask-${idx}`"
            :class="`${el}__subTask`"
            @click="openContentSheet(subTask.taskId, task.taskId)"
          >
            <Label v-if="testing" :class="`${el}__debug`" :label="subTask.taskId" />
            <BaseRadio
              :class="`${el}__baseRadio`"
              :label="subTask.headline"
              :checked="subTask.checked"
              checkmark
            />
            <Label
              v-if="subTask.optional"
              :label="$t('taskScheduler.label.optional')"
              color="secondaryYellow"
              :class="`${el}__subTaskLabel`"
            />
            <Link :class="`${el}__subTaskLink`" icon="arrow-right-line" iconPos="right">
              {{ $t('taskScheduler.edit') }}
            </Link>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div :class="`${el}__container`" @click="openContentSheet(task.taskId)">
        <div :class="`${el}__indicator`">
          <BaseRadio :checked="task.checked" />
        </div>

        <div :class="`${el}__inner`">
          <div :class="`${el}__content`">
            <div :class="`${el}__headline`">
              <Headline v-if="current && $matchMedia.l" size="xs" noMargin>
                {{ task.headline }}
              </Headline>
              <Copy v-else size="l" element="div" color="primaryBlue" bold>
                {{ task.headline }}
              </Copy>
              <Label
                v-if="task.optional"
                :label="$t('taskScheduler.label.optional')"
                color="secondaryYellow"
              />
            </div>

            <Copy
              v-if="task.copy && current"
              :staticClass="`${el}__subline`"
              element="div"
              color="tertiaryLight"
            >
              {{ task.copy }}
            </Copy>
          </div>
          <div :class="`${el}__edit`">
            <Link v-if="isAuthenticated" icon="arrow-right-line" iconPos="right">
              {{ $t('taskScheduler.edit') }}
            </Link>
            <Button
              v-else
              :label="$t('careLevelCalculator.analysis.pflegegradeinstufung.unlock')"
              @click="
                $emit(
                  'onTrackingClick',
                  $t('careLevelCalculator.analysis.pflegegradeinstufung.unlock'),
                  'button'
                )
              "
              additionalIconName="lock-line"
              additionalIconPos="left"
              small
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Headline from '@/objects/Headline';
import Link from '@/objects/Link';
import Button from '@/objects/Button';
import Copy from '@/objects/Copy';
import Icon from '@/objects/Icon';
import Label from '@/objects/Label';
import BaseRadio from '@/objects/Forms/BaseRadio';
import Doughnut from '@/components/Charts/Doughnut';

import * as colors from '@/styles/base/_colors.scss';

import { CONTENT_SHEET_CONTENT } from '@/store/actions/contentSheet';

import { mapFieldsAuth } from '@/store/mapFields';

export default {
  name: 'Tasks',
  components: {
    BaseRadio,
    Headline,
    Link,
    Button,
    Copy,
    Icon,
    Label,
    Doughnut,
  },
  props: {
    collapsibleIsOpen: {
      type: Boolean,
      default: false,
    },
    isCollapsible: {
      type: Boolean,
      default: false,
    },
    /** The current prop means the rose variant */
    current: {
      type: Boolean,
      default: false,
    },
    hide: {
      type: Boolean,
      default: false,
    },
    isOptionalTask: {
      type: Boolean,
      default: false,
    },
    task: {
      id: {
        type: [Number, String],
        required: true,
      },
      optional: {
        type: Boolean,
        default: false,
      },
      checked: {
        type: Boolean,
        default: false,
      },
      headline: {
        type: String,
        required: true,
      },
      copy: {
        type: String,
      },
      subTasks: {
        type: Array,
      },
    },
  },
  watch: {
    hide: function () {
      if (this.hide) {
        this.close();
      }
    },
  },
  computed: {
    ...mapFieldsAuth(['isAuthenticated']),

    chckedSubtasks() {
      const match = this.task.subTasks.filter((st) => st.checked);
      return match.length;
    },
    countSubtasks() {
      if (!this.task.subTasks) {
        return 0;
      }

      return this.task.subTasks.length;
    },
    hasSubTasks() {
      return this.task.subTasks && this.task.subTasks.length > 0;
    },
    visibleSubtask() {
      return this.task.subTasks.filter((st) => !st.checked)[0] || false;
    },
  },
  data() {
    return {
      testing: !!process.env.VUE_APP_TESTING,
      collapsible: this.collapsibleIsOpen,
      colorSupportSuccess: colors.default.supportSuccess,
      scrollHeight: 0,
      collapsibleHeight: this.collapsibleIsOpen ? 10000 : 0, // 10000 to prevent start animation
      el: 'c-tasks',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
          [`${el}--current`]: this.current,
          [`${el}--collapsible`]: this.isCollapsible,
          [`${el}--collapsibleIsOpen`]: this.collapsible,
          [`${el}--hide`]: this.hide,
        };
      },
    };
  },
  mounted() {
    this.setHeights();

    if (this.hide) {
      this.close();
    }

    window.addEventListener('resize', this.setHeights);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setHeights);
  },
  methods: {
    handleClick() {
      if (this.isCollapsible) {
        this.toggleCollapsible();
      } else {
        if (this.visibleSubtask.taskId) {
          /**
           * Der Fall tritt im Widget auf, wenn die Aufgabe mehrere Subtasks hat.
           * Dann soll immer die erste, noch nicht erledigte Aufgabe, geöffnet werden.
           */
          this.openContentSheet(this.visibleSubtask.taskId, this.task.taskId);
        } else {
          this.openContentSheet(this.task.taskId);
        }
      }
    },

    setHeights() {
      if (this.$refs.subTasks?.scrollHeight !== this.scrollHeight) {
        this.scrollHeight = this.$refs.subTasks?.scrollHeight || 0;
        if (this.collapsibleHeight > 0 || this.collapsibleIsOpen) {
          this.collapsibleHeight = this.scrollHeight;
        }
      }
    },

    close() {
      this.$el.style.maxHeight = this.$el.scrollHeight + 'px';
      setTimeout(() => {
        this.$el.style.maxHeight = '0px';
      }, 100);
    },

    toggleCollapsible() {
      this.collapsibleHeight = this.collapsibleHeight === 0 ? this.scrollHeight : 0;
      this.collapsible = !this.collapsible;
    },

    openContentSheet(taskId, parentTaskId = null) {
      // const taskIdTEST = 'P2-40';
      const taskSheet = {
        sheetId: `tasks/Index`,
        data: { taskId, parentTaskId, isOptionalTask: this.isOptionalTask },
      };

      const registerSheet = {
        sheetId: 'register/Index',
      };

      this.$store.dispatch(CONTENT_SHEET_CONTENT, this.isAuthenticated ? taskSheet : registerSheet);
    },
  },
};
</script>
